import type { JsonValue } from 'type-fest';

import { prepareAndMakeRequest } from '@frontend/api';

import { keysToSnakeCase } from '../utils/stringUtils';

const websocket = {
  init: (): Promise<{ socketToken: string }> =>
    prepareAndMakeRequest.post<{ socketToken: string }>('/websockets/init'),
};

export default {
  acceptTerms: (terms: string): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>(`/user_agreements/terms/${terms}`),

  getActiveUserAgreement: (termsType: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(
      `/user_agreements/terms/active/${termsType}`,
    ),

  getTerms: (termsType: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/user_agreements/terms/${termsType}`),

  getTokenAfterSSO: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/token`),

  /**
   * Logs in with a password. A user migrated to Authentik gets
   * `next: 'oidc'` and the URL to redirect the browser to instead of a token.
   */
  login: (body: {
    password: string;
    username: string;
    next?: string;
  }): Promise<{
    token?: { key: string; expiry: string };
    next?: string;
    oidcUrl?: string;
  }> =>
    prepareAndMakeRequest.post<{
      token?: { key: string; expiry: string };
      next?: string;
      oidcUrl?: string;
    }>('/login', { body }),

  changePassword: (body: {
    currentPassword: string;
    newPassword: string;
    newPasswordConf: string;
  }): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.post<{ token: { key: string; expiry: string } }>(
      '/users/change_password',
      {
        body,
      },
    ),

  initiatePasswordChange: (): Promise<{ passwordChangeUrl: string }> =>
    prepareAndMakeRequest.post<{ passwordChangeUrl: string }>(
      '/users/change_password/initiate/',
    ),

  resetPassword: (body: { email: string }): Promise<string> =>
    prepareAndMakeRequest.post<string>('/forgot_password', { body }),

  requestLoginInfo: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>('/users/login_activity'),

  requestTwoFactorAuthSecret: (id: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/users/two_factor_auth/${id}`),

  verifyTwoFactorAuthToken: (
    id: string,
    token: string,
    secret: string,
  ): Promise<{ isValid: true }> =>
    prepareAndMakeRequest.post<{ isValid: true }>(
      `/users/two_factor_auth/${id}`,
      { body: { token, secret } },
    ),

  websocket,

  logoutAll: (): Promise<JsonValue> =>
    prepareAndMakeRequest.delete<JsonValue>('/users/log_out_all'),

  twoFactorLogin: ({
    username,
    password,
    token,
    next,
  }: {
    username: string;
    password: string;
    token: string;
    next?: string;
  }): Promise<{
    token?: string;
    next?: string;
    oidcUrl?: string;
    passwordResetRequired?: boolean;
  }> =>
    prepareAndMakeRequest.post<{
      token?: string;
      next?: string;
      oidcUrl?: string;
      passwordResetRequired?: boolean;
    }>('/login', {
      body: { username, password, token, next },
    }),

  initiateTotpEnrollment: (id: string): Promise<{ totpEnrollUrl: string }> =>
    prepareAndMakeRequest.post<{ totpEnrollUrl: string }>(
      `/users/two_factor_auth/${id}/initiate/`,
    ),

  setPassword: (
    key: string,
    body: Record<string, JsonValue>,
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>(`/set_password/${key}`, { body }),

  profile: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>('/users/profile', {
      retryOptions: { maxRetries: 10 },
    }),

  updateUser: (
    username: string,
    body: { firstName: string; lastName: string },
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.put<JsonValue>(`/users/${username}`, { body }),

  // TODO: move to supplierService or something?
  getSupplierFileUrl: (
    groupId: string,
    token: string,
  ): Promise<{ url: string }> =>
    prepareAndMakeRequest.get<{ url: string }>(
      `/suppliers/${groupId}/download`,
      { query: { token } },
    ),

  signUp: (body: { isSigningUp: boolean }): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>('/registration', { body }),

  /**
   * Asks the backend how a given email should authenticate. Returns
   * `next: 'password'` for password auth, or `next: 'oidc' | 'saml'` with the
   * URL to redirect the browser to. Also establishes the session that carries
   * the OIDC PKCE state, so this must be called with credentials included.
   */
  discover: (body: {
    email: string;
    next?: string;
  }): Promise<{
    next: string;
    oidcUrl?: string;
    ssoUrl?: string;
  }> =>
    prepareAndMakeRequest.post<{
      next: string;
      oidcUrl?: string;
      ssoUrl?: string;
    }>('/auth/discover', { body }),

  discoverSSO: (body: { email: boolean }): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>('/sso/discover', { body }),

  getUserGroups: (params: {
    search: string;
    pageNumber: string;
  }): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>('/users/super_users/user_groups', {
      query: params,
    }),

  updateActiveGroup: (
    userId: string,
    data: { activeGroupId: string },
  ): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.patch<{ token: { key: string; expiry: string } }>(
      `/users/super_users/${userId}/active_group`,
      { body: keysToSnakeCase(data) },
    ),

  getActiveUserGroupMembers: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/users/groups_members/`),

  switchActiveUserGroup: (
    nextGroupMemberId: string,
  ): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.patch<{ token: { key: string; expiry: string } }>(
      `/users/active_group_member/`,
      { body: { groupMemberId: nextGroupMemberId } },
    ),
};

export const featureFlags = {
  get: (groupLocator: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(
      `/users/groups/${groupLocator}/feature_flags/`,
    ),
  update: (
    groupLocator: string,
    data: Record<string, JsonValue>,
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.put<JsonValue>(
      `/users/groups/${groupLocator}/feature_flags/`,
      { body: keysToSnakeCase(data) },
    ),
};
